import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import { colors, pipelineColors } from "../utils/datosTheme";
import {
  formatDateShort,
  formatMoney,
  formatMonthLabel,
  formatNumber,
} from "../utils/datosFormatters";

export default function GeneralDashboard({ data }) {
  const monthly = useMemo(
    () => (data?.mensual || []).map((item) => ({ ...item, label: formatMonthLabel(item.mes) })),
    [data]
  );
  const pipeline = useMemo(
    () => (data?.pipeline || []).map((item) => ({
      ...item,
      label: item.estado.charAt(0) + item.estado.slice(1).toLowerCase(),
    })),
    [data]
  );
  const kpis = data?.kpis || {};
  const currency = kpis.moneda_principal;
  const periodLabel = data?.desde && data?.hasta
    ? `${formatDateShort(data.desde)} al ${formatDateShort(data.hasta)}`
    : "período seleccionado";
  const hasActivityData = monthly.some((item) => (
    Number(item.consultas_nuevas || 0) > 0 ||
    Number(item.ventas_cerradas || 0) > 0 ||
    Number(item.bajas || 0) > 0 ||
    Number(item.futuros || 0) > 0
  ));
  const hasPipelineData = pipeline.some((item) => Number(item.total || 0) > 0);
  const moneyHelper = Array.isArray(kpis.monto_vendido_por_moneda) && kpis.monto_vendido_por_moneda.length > 1
    ? kpis.monto_vendido_por_moneda.map((item) => `${item.moneda}: ${formatMoney(item.total, item.moneda)}`).join(" · ")
    : null;
  const kpiDescriptions = {
    consultas_nuevas: `Leads iniciados del ${periodLabel}`,
    consultas_activas: "Leads abiertos actualmente",
    ventas_cerradas: "Ventas registradas en el período",
    conversion_pct: "Ventas cerradas / consultas nuevas",
    monto_vendido: `${formatNumber(kpis.ventas_cerradas)} vehículos vendidos`,
    bajas: "Consultas cerradas como baja",
    futuros: "Consultas cerradas o programadas como futuro",
    mejor_vendedor: "Score: 35% ventas + 35% monto + 30% conversión",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "consultas_nuevas",
          "consultas_activas",
          "ventas_cerradas",
          "conversion_pct",
          "monto_vendido",
          "bajas",
          "futuros",
          "mejor_vendedor",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={id === "mejor_vendedor" ? (kpis[id] || "Sin datos") : kpis[id]}
            currency={currency}
            helper={id === "monto_vendido" ? moneyHelper : null}
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.25fr 1fr" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel title="Consultas, ventas, bajas y futuros por mes" empty={!hasActivityData}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              <Bar dataKey="consultas_nuevas" name="Consultas" fill={colors.blue} radius={[4, 4, 0, 0]} />
              <Bar dataKey="ventas_cerradas" name="Ventas" fill={colors.green} radius={[4, 4, 0, 0]} />
              <Bar dataKey="bajas" name="Bajas" fill={colors.coral} radius={[4, 4, 0, 0]} />
              <Bar dataKey="futuros" name="Futuros" fill={colors.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Pipeline por estado" empty={!hasPipelineData}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pipeline}
                dataKey="total"
                nameKey="label"
                innerRadius="48%"
                outerRadius="76%"
                paddingAngle={2}
                label={({ label, total }) => `${label}: ${total}`}
              >
                {pipeline.map((entry) => (
                  <Cell key={entry.estado} fill={pipelineColors[entry.estado] || colors.teal} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>
    </>
  );
}
